import {
  type GetStaticPropsContext,
  type GetStaticPaths,
  type NextPage,
  type InferGetStaticPropsType,
} from "next";
import Link from "next/link";
import React from "react";
import CategoryComponents from "~/components/CategoryComponents";
import GridLoop from "~/components/GridLoop";
import Layout from "~/components/Layout";
import { usePagination } from "~/components/Pagination";
import Pagination from "~/components/Pagination/Pagination";
import {
  getCategoriesSlug,
  getNameCategory,
  type getNameSubCategoryType,
  getParentCategory,
  getSubCategory,
} from "~/lib/db/category";
import { type LoopPostReturnType } from "~/lib/db/loops";
import {
  getArticleByCategory,
  getPostsMetadata,
  getThumbnail,
} from "~/lib/db/posts";
import { ISSUES_TYPE, type ArticlePreviewType } from "~/types";
import { postExcerpt } from "~/utils";
const DEFAULT_ITEMS_PER_PAGE = 18;

const Category: NextPage<InferGetStaticPropsType<typeof getStaticProps>> = ({
  category_name,
  parent_category_slug,
  category_description,
  parent_category_name,
  articles,
  subCategory,
}) => {
  const articlessShow = usePagination(articles, DEFAULT_ITEMS_PER_PAGE);
  const imageShow =
    category_name == "Recensioni" ||
    category_name == "Invito alla lettura" ||
    category_name == "Scritti di Daisaku Ikeda"
      ? false
      : true;
  const displaySubcategories = subCategory.map((el, index) => {
    return {
      id: index,
      item: {
        term: el,
      },
    };
  });
  return (
    <>
      <Layout title={category_name}>
        <section
          className={`${
            subCategory.length > 0 &&
            !parent_category_slug.includes("lo-studio")
              ? "bg-green"
              : ""
          } -mt-12 w-full`}
        >
          <div className="mx-auto max-w-box px-4 pb-32 pt-24">
            {parent_category_name !== category_name && (
              <h3 className="mb-4 text-lg font-bold">
                <Link href={`/c/${parent_category_slug}`} className="font-bold">
                  {parent_category_name}
                </Link>
              </h3>
            )}
            <div className="mb-20 lg:mb-32">
              <h1
                className={`${
                  subCategory.length > 0 &&
                  !parent_category_slug.includes("lo-studio") &&
                  "text-white"
                } text-4xl font-light md:text-6xl`}
              >
                {category_name}
              </h1>
              {category_description && (
                <p
                  className={`${
                    subCategory.length > 0 &&
                    !parent_category_slug.includes("lo-studio") &&
                    "text-white"
                  } mb-10 mt-10 text-lg font-medium md:mb-24`}
                >
                  {category_description}
                </p>
              )}
            </div>
            {subCategory.length > 0 &&
            !parent_category_slug.includes("lo-studio") ? (
              <div className="grid items-start gap-24 md:grid-cols-2 lg:grid-cols-3">
                {displaySubcategories.map((item, index) => (
                  <CategoryComponents
                    key={index}
                    data={item}
                    tax={ISSUES_TYPE.categories}
                  />
                ))}
              </div>
            ) : (
              <GridLoop
                gridloop={articlessShow as ArticlePreviewType[]}
                categoria={"category"}
                imageShow={imageShow}
              />
            )}
          </div>
        </section>
        <div
          className={`mx-auto mt-6 max-w-box px-4 text-center ${
            (subCategory.length > 0 &&
            !parent_category_slug.includes("lo-studio")
              ? displaySubcategories.length
              : articles.length) > DEFAULT_ITEMS_PER_PAGE
              ? ""
              : "hidden"
          }`}
        >
          <Pagination
            totalResults={
              subCategory.length > 0 &&
              !parent_category_slug.includes("lo-studio")
                ? displaySubcategories.length
                : articles.length
            }
            itemsPerPage={DEFAULT_ITEMS_PER_PAGE}
          />
        </div>
      </Layout>
    </>
  );
};

export default Category;

export const getStaticPaths: GetStaticPaths<{
  category: string;
}> = async () => {
  const categoriesSlug = await getCategoriesSlug(ISSUES_TYPE.categories);

  return {
    paths: categoriesSlug.map((category) => ({
      params: { category: category.slug },
    })), //indicates that no page needs be created at build time
    fallback: "blocking", //indicates the type of fallback
  };
};

export const getStaticProps = async ({ params }: GetStaticPropsContext) => {
  if (!params) return { notFound: true };

  try {
    const category = await getNameCategory(params.category as string);
    if (!category) return { notFound: true };
    const [parent, subCategory, articleByCategory] = await Promise.all([
      getParentCategory(category.parent),
      getSubCategory(category.term_id),
      getArticleByCategory(category.term_id),
    ]);

    const articlesId = articleByCategory.map((el) => el.ID);

    const [metaData, thumbnail] = await Promise.all([
      articlesId.length > 0
        ? getPostsMetadata(articlesId)
        : Promise.resolve([]),
      articlesId.length > 0 ? getThumbnail(articlesId) : Promise.resolve([]),
    ]);

    const mapPostsForClient = (
      posts: LoopPostReturnType[]
    ): ArticlePreviewType[] =>
      posts.map(
        ({
          ID,
          post_title,
          post_name,
          post_excerpt,
          post_content,
          post_date,
        }) => ({
          post_name,
          post_title,
          post_excerpt:
            post_excerpt?.trim() != ""
              ? postExcerpt(post_excerpt)
              : postExcerpt(
                  post_content
                    ?.replace(/<\/?[^>]+(>|$)/g, "")
                    ?.replace(/\[\/{0,1}et_pb_.*\]/g, "")
                ),
          meta: metaData[Number(ID)] != null ? metaData[Number(ID)] : null,
          thumbnail:
            thumbnail[Number(ID)]?.thumbnail != null
              ? thumbnail[Number(ID)]?.thumbnail
              : null,
          alt:
            thumbnail[Number(ID)]?.alt != null
              ? thumbnail[Number(ID)]?.alt
              : null,
          post_date: post_date.toISOString(),
        })
      );

    const mapCategoryForClient = (
      posts: getNameSubCategoryType[]
    ): getNameSubCategoryType[] =>
      posts.map(({ slug, name, description, img }) => ({
        slug,
        name,
        description,
        img,
      }));

    return {
      props: {
        category_name: category.name,
        category_description: category.description,
        category_slug: params.category,
        parent_category_slug: parent?.slug || params.category,
        parent_category_description: parent?.description || "",
        parent_category_name:
          parent?.name.replace("&amp;", " & ") ||
          category.name.replace("&amp;", " & "),
        subCategory: mapCategoryForClient(subCategory),
        articles: mapPostsForClient(articleByCategory),
      },
      revalidate: 20 * 60,
    };
  } catch (error) {
    console.log("Error fetching static props for", params.category, error);
    return { notFound: true };
  }
};
